import * as React from 'react';
import { cn } from '@/lib/utils';

// ============ Types ============

interface DeviceMockupProps {
  children: React.ReactNode;
  className?: string;
  scale?: number;
}

// ============ Component ============

export function DeviceMockup({
  children,
  className,
  scale = 0.7,
}: DeviceMockupProps) {
  return (
    <div
      className={cn('flex items-center justify-center', className)}
      style={{ transform: `scale(${scale})` }}
    >
      {/* Phone frame */}
      <div className="relative w-[280px] bg-black rounded-[40px] p-3 shadow-2xl">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-black rounded-b-2xl z-10" />

        {/* Screen container */}
        <div className="relative bg-white rounded-[32px] overflow-hidden">
          {/* Status bar */}
          <div className="h-11 bg-white flex items-end justify-between px-6 pb-1">
            <span className="text-xs font-semibold">9:41</span>
            <div className="flex items-center gap-1">
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M1.5 9.5l10.5 10.5 10.5-10.5-2.5-2.5-8 8-8-8z" />
              </svg>
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M2 19h20v-4h-20v4zm2-2h2v-1h-2v1zm4 0h2v-1h-2v1zm0-6h16v-4h-16v4zm2-2h2v-1h-2v1z" />
              </svg>
              <div className="flex items-center gap-0.5">
                <div className="w-6 h-3 border border-current rounded-sm flex items-center justify-end pr-0.5">
                  <div className="w-4 h-2 bg-current rounded-sm" />
                </div>
              </div>
            </div>
          </div>

          {/* Screen content */}
          <div className="h-[500px] overflow-hidden bg-gray-50">
            {children}
          </div>

          {/* Home indicator */}
          <div className="h-8 bg-white flex items-center justify-center">
            <div className="w-32 h-1 bg-gray-300 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Simplified Phone Preview ============

interface PhonePreviewProps {
  children: React.ReactNode;
  className?: string;
}

export function PhonePreview({ children, className }: PhonePreviewProps) {
  return (
    <div className={cn('w-full max-w-[260px] mx-auto', className)}>
      {/* Simple phone frame */}
      <div className="relative bg-gray-900 rounded-[32px] p-2.5 shadow-xl">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-900 rounded-b-2xl z-10" />

        {/* Screen */}
        <div className="relative bg-white rounded-[26px] overflow-hidden">
          {/* Status bar */}
          <div className="h-8 bg-white flex items-center justify-center">
            <span className="text-[11px] font-medium">9:41</span>
          </div>

          {/* Content */}
          <div className="min-h-[380px] bg-gray-50">{children}</div>

          {/* Home indicator */}
          <div className="h-6 bg-white flex items-center justify-center">
            <div className="w-24 h-1 bg-gray-300 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}
